import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FilePlus2, Search, SlidersHorizontal, Pencil, Send, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fmtDateTime, imgSrc } from "../lib/api";
import { STATUS_LABELS } from "../lib/brand";
import { DemoBadge } from "../components/site/ArticleCard";
import { PageTitle, Empty, inputCls, btnAccent, btnGhost } from "./ui";
import { BrandLoader } from "../components/site/Logo";
import { PublishDialog, SimpleStatus } from "./PublishDialog";

const NewBtn = () => {
  const { can } = useAuth();
  return can("articles.create") ? <Link to="/admin/articles/new" className={btnAccent} data-testid="new-article-button"><FilePlus2 className="h-4 w-4" /> Novi članak</Link> : null;
};

const BULK = { in_review: "Pošalji na pregled", fact_check: "Pošalji na provjeru činjenica", archived: "Arhiviraj", draft: "Vrati iz arhive u nacrt", unpublished: "Povuci s portala" };

const Bulk = ({ ids, done }) => {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const run = async () => {
    if (to === "unpublished" && note.trim().length < 5) return toast.error("Za povlačenje upišite razlog (najmanje 5 znakova).");
    if (!window.confirm(`${BULK[to]}: ${ids.length} članak/a?`)) return;
    try {
      const { data } = await api.post("/admin/articles/bulk", { ids, to, note });
      const failed = data.results.filter((r) => !r.ok);
      if (data.done) toast.success(`Uspješno: ${data.done} od ${ids.length}`);
      else toast.error("Nijedan članak nije promijenjen.");
      failed.forEach((r) => toast.error(`${r.headline || r.id}: ${r.error}`));
      done();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-pulse/40 bg-card p-3 text-sm" data-testid="bulk-bar">
      <span className="font-semibold" data-testid="bulk-count">Odabrano: {ids.length}</span>
      <select value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} sm:!w-64`} data-testid="bulk-action"><option value="">— skupna radnja —</option>{Object.entries(BULK).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      {to === "unpublished" && <input placeholder="Razlog povlačenja" value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} sm:!w-64`} data-testid="bulk-note" />}
      <button disabled={!to} onClick={run} className={btnAccent} data-testid="bulk-apply">Primijeni</button>
    </div>
  );
};

const CHIPS = [["", "Svi"], ["draft,needs_revision", "Nacrti"], ["in_review,fact_check,approved", "Za pregled"], ["scheduled", "Zakazani"], ["published", "Objavljeni"]];

const ArticleCardRow = ({ a, selectable, sel, toggle, onPublish }) => {
  const { can } = useAuth();
  return (
    <li className="group flex items-center gap-4 p-3 transition-colors hover:bg-muted/40 sm:p-4" data-testid={`article-row-${a.id}`}>
      {selectable && <input type="checkbox" checked={sel} onChange={toggle} aria-label={`Odaberi ${a.headline}`} data-testid={`select-article-${a.id}`} />}
      <Link to={`/admin/articles/${a.id}`} className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-muted sm:h-20 sm:w-28" tabIndex={-1}>
        {a.hero_image_url ? <img src={imgSrc(a.hero_image_url)} alt="" className="h-full w-full object-cover" loading="lazy" /> : <span className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-5 w-5" /></span>}
      </Link>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2"><SimpleStatus status={a.status} /><span className="text-xs font-semibold text-pulse">{a.category?.name}</span><DemoBadge show={a.is_demo} /></div>
        <Link to={`/admin/articles/${a.id}`} className="line-clamp-2 font-display text-base font-semibold leading-snug hover:underline sm:text-lg" data-testid={`article-title-${a.id}`}>{a.headline}</Link>
        <p className="mt-1 text-xs text-muted-foreground">{a.author_name} · {fmtDateTime(a.updated_at)}</p>
      </div>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
        <Link to={`/admin/articles/${a.id}`} className={`${btnGhost} px-3`} aria-label="Uredi" data-testid={`article-edit-${a.id}`}><Pencil className="h-4 w-4" /><span className="hidden md:inline">Uredi</span></Link>
        {can("articles.publish") && !("published archived".includes(a.status)) && <button type="button" onClick={() => onPublish(a.id)} className={`${btnAccent} px-3`} aria-label="Objavi" data-testid={`article-publish-${a.id}`}><Send className="h-4 w-4" /><span className="hidden md:inline">Objavi</span></button>}
      </div>
    </li>
  );
};

export function ArticlesList() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const cat = params.get("kategorija") || "";
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState([]);
  const [tick, setTick] = useState(0);
  const [adv, setAdv] = useState(false);
  const [pub, setPub] = useState(null);
  const [page, setPage] = useState(1);
  const { data: cats } = useFetch("/admin/categories");
  const { data, loading } = useFetch(`/admin/articles?${new URLSearchParams({ page, ...(status && { status }), ...(cat && { category_id: cat }), ...(query && { q: query }) })}`, [tick]);
  const setP = (k, v) => { const p = Object.fromEntries(params); if (v) p[k] = v; else delete p[k]; setParams(p); };
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  useEffect(() => {
    setSel([]);
    setPage(1);
  }, [status, query, cat]);
  const all = data?.items.length > 0 && data.items.every((a) => sel.includes(a.id));
  return (
    <div data-testid="admin-articles-page">
      <PageTitle title="Članci" sub={data ? `${data.total} članaka` : ""} actions={<NewBtn />} />
      <div className="mb-4 flex flex-col gap-3 md:flex-row">
        <form onSubmit={(e) => { e.preventDefault(); setQuery(q); }} className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretraga po naslovu ili tekstu…" className={`${inputCls} py-2.5 pl-9`} data-testid="articles-search-input" />
        </form>
        <select value={cat} onChange={(e) => setP("kategorija", e.target.value)} className={`${inputCls} md:!w-56`} data-testid="articles-category-filter"><option value="">Sve kategorije</option>{(cats || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <button type="button" onClick={() => setAdv(!adv)} aria-expanded={adv} className={btnGhost} data-testid="articles-advanced-toggle"><SlidersHorizontal className="h-4 w-4" /> Napredno</button>
      </div>
      <div className="-mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-1" role="tablist" data-testid="articles-status-chips">
        {CHIPS.map(([k, l]) => <button key={l} type="button" role="tab" aria-selected={status === k} onClick={() => setP("status", k)} className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${status === k ? "border-navy bg-navy text-white dark:border-pulse dark:bg-pulse" : "border-border bg-card text-muted-foreground hover:text-foreground"}`} data-testid={`articles-chip-${l.toLowerCase().replace(" ", "-")}`}>{l}</button>)}
      </div>
      {adv && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 p-3 text-sm" data-testid="articles-advanced-panel">
          <select value={status} onChange={(e) => setP("status", e.target.value)} className={`${inputCls} sm:!w-56`} data-testid="articles-status-filter">
            <option value="">Svi statusi (detaljno)</option>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {data?.items.length > 0 && <label className="flex items-center gap-2"><input type="checkbox" checked={all} onChange={() => setSel(all ? [] : data.items.map((a) => a.id))} data-testid="select-all-articles" /> Odaberi sve za skupnu radnju</label>}
          <Link to="/admin/workflow" className="text-xs font-semibold text-pulse hover:underline" data-testid="articles-workflow-link">Urednički tok (kanban)</Link>
        </div>
      )}
      {sel.length > 0 && <Bulk ids={sel} done={() => { setSel([]); setTick((t) => t + 1); }} />}
      {loading && !data ? <BrandLoader /> : data?.items.length ? (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card" data-testid="articles-table">
          {data.items.map((a) => <ArticleCardRow key={a.id} a={a} selectable={adv} sel={sel.includes(a.id)} toggle={() => toggle(a.id)} onPublish={setPub} />)}
        </ul>
      ) : <Empty testId="articles-empty">Nema članaka za odabrani filter.</Empty>}
      {data?.pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-sm" data-testid="articles-pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className={btnGhost} data-testid="articles-prev-page">Prethodna</button>
          <span data-testid="articles-page-info">Stranica {data.page} od {data.pages}</span>
          <button type="button" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className={btnGhost} data-testid="articles-next-page">Sljedeća</button>
        </div>
      )}
      {pub && <PublishDialog articleId={pub} onClose={() => setPub(null)} onDone={() => setTick((t) => t + 1)} />}
    </div>
  );
}

const COLS = ["draft", "needs_revision", "in_review", "fact_check", "approved", "scheduled", "published"];

export function Workflow() {
  const { data, loading } = useFetch("/admin/articles?page=1");
  return (
    <div data-testid="admin-workflow-page">
      <PageTitle title="Urednički tok" sub="Nacrt → Pregled → Provjera činjenica → Odobreno → Objavljeno" actions={<NewBtn />} />
      {loading ? <BrandLoader /> : (
        <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
          {COLS.map((s) => {
            const items = data.items.filter((a) => a.status === s);
            return (
              <section key={s} className="rounded-lg border border-border bg-muted/30 p-3" data-testid={`workflow-column-${s}`}>
                <h2 className="mb-3 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-muted-foreground">{STATUS_LABELS[s]} <span>{items.length}</span></h2>
                <div className="space-y-2">
                  {items.map((a) => (
                    <Link key={a.id} to={`/admin/articles/${a.id}`} className="block rounded-md border border-border bg-card p-3 text-sm transition-colors hover:border-pulse/60" data-testid={`workflow-card-${a.id}`}>
                      <p className="font-medium leading-snug">{a.headline}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{a.author_name} · {a.category?.name}</p>
                    </Link>
                  ))}
                  {!items.length && <p className="py-4 text-center text-xs text-muted-foreground">—</p>}
                </div>
              </section>
            );
          })}
        </div>
      )}
      <p className="mt-4 text-xs text-muted-foreground">Prikazano je posljednjih 25 uređenih članaka.</p>
    </div>
  );
}
